import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchPost } from '@app/src/api/fetchHooks'
import { Periods } from '@app/src/components/CreateNewRequestModal/PeriodSelector/PeriodSelector'
import PreviousNumberAnswersContext from '@app/src/context/PreviousNumberAnswersContext'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { equalsCaseInsensitive } from '@app/src/utils/helpersTs'
import { QuestionTypes } from '@app/src/wf-constants'
import type { ReactNode } from 'react'
import React, { useMemo } from 'react'

type PreviousNumberAnswersContextProviderProps = {
  children: ReactNode
  responseItems: ResponseItem[]
}

interface PreviousAnswerQuery {
  questionId: number
  providerOrganizationId: number
  earlierThanDate: string
}
const PreviousNumberAnswersContextProvider: React.FC<PreviousNumberAnswersContextProviderProps> = ({
  children,
  responseItems,
}: PreviousNumberAnswersContextProviderProps) => {
  const previousAnswerQueries: PreviousAnswerQuery[] = useMemo(
    () =>
      responseItems
        .filter(
          ri =>
            ri.requestItem &&
            ri.response?.request &&
            equalsCaseInsensitive(ri.requestItem?.questionType?.name ?? '', QuestionTypes.Number) &&
            !ri.cannotAnswer &&
            ri?.response.request.periodType !== Periods.NoPeriod &&
            ri.creatorOrganizationId,
        )
        .map(ri => ({
          questionId: ri.requestItem.questionId,
          providerOrganizationId: ri.creatorOrganizationId ?? 0,
          earlierThanDate: ri.response.request.periodStartsAt ?? '',
        })),
    [responseItems],
  )

  const { data: previousNumberAnswers } = useFetchPost<ResponseItem[], PreviousAnswerQuery[]>({
    key: FetchKey.PreviousAnswers,
    endpoint: endpoints.previousAnswers,
    payload: previousAnswerQueries,
    options: { enabled: previousAnswerQueries.length > 0 },
    shouldShowErrorNotification: false,
  })
  return (
    <PreviousNumberAnswersContext.Provider value={{ previousNumberAnswers: previousNumberAnswers ?? [] }}>
      {children}
    </PreviousNumberAnswersContext.Provider>
  )
}

export default PreviousNumberAnswersContextProvider
